import { colorSwatch } from "./colorSwatch.js";
import { materialColorAssignment, materialColorNeeds } from "./materialColors.js";
const NO_SUPPLIER_LABEL = "Sin proveedor";

export function buildNeedsBySupplier(model = {}) {
  const groups = new Map();

  const ensureGroup = (supplierName, processOrder = Infinity) => {
    if (!groups.has(supplierName)) {
      groups.set(supplierName, { supplier: supplierName, lines: new Map(), supplierTotalCost: 0, processOrder });
    }
    const group = groups.get(supplierName);
    group.processOrder = Math.min(group.processOrder, processOrder);
    return group;
  };

  for (const item of model.items || []) {
    const cuttingMaterialIds = new Set((item.cutting || []).flatMap((process) =>
      (process.materials || []).map((material) => material.id).filter(Boolean)));
    for (const material of item.supplierMaterials || item.materials || []) {
      const supplierName = material.supplier_name?.trim() || NO_SUPPLIER_LABEL;
      const processOrder = item.supplierProcessOrder?.[material.process_source_line_id]
        ?? (item.processes || []).find((process) => process.source_line_id === material.process_source_line_id)?.sort_order;
      const group = ensureGroup(supplierName, processOrder);
      const reference = material.supplier_ref || material.material_code || "";
      const lineKey = JSON.stringify(["material", material.material_id || material.material_code || material.name, reference, material.name, material.unit]);
      if (!group.lines.has(lineKey)) {
        group.lines.set(lineKey, {
          name: material.name,
          reference,
          kind: "Material",
          unit: material.unit,
          colorNeeds: new Map(),
          totalQuantity: 0,
          totalCost: 0,
        });
      }
      const aggregated = group.lines.get(lineKey);
      aggregated.totalQuantity += Number(material.total_quantity || 0);
      aggregated.totalCost += Number(material.total_cost || 0);
      if (cuttingMaterialIds.has(material.source_line_id) || material.requires_color !== false) {
        for (const color of materialColorNeeds(material, item.colorLines)) {
          const key = JSON.stringify([color.color, color.pending]);
          if (!aggregated.colorNeeds.has(key)) aggregated.colorNeeds.set(key, { ...color, totalQuantity: 0 });
          aggregated.colorNeeds.get(key).totalQuantity += color.totalQuantity;
        }
      }
      group.supplierTotalCost += Number(material.total_cost || 0);
    }

    for (const process of item.processes || []) {
      const supplierName = process.supplier_name?.trim() || NO_SUPPLIER_LABEL;
      const group = ensureGroup(supplierName, item.supplierProcessOrder?.[process.source_line_id] ?? process.sort_order);
      const lineKey = `process::${process.name}`;
      if (!group.lines.has(lineKey)) {
        group.lines.set(lineKey, {
          name: process.name,
          kind: "Proceso",
          unit: "prendas",
          totalQuantity: 0,
          totalCost: 0,
        });
      }
      const aggregated = group.lines.get(lineKey);
      aggregated.totalQuantity += Number(process.total_quantity || 0);
      aggregated.totalCost += Number(process.total_cost || 0);
      group.supplierTotalCost += Number(process.total_cost || 0);
    }
  }

  const result = [...groups.values()].map((group) => ({
    supplier: group.supplier,
    supplierTotalCost: Number(group.supplierTotalCost.toFixed(4)),
    lines: [...group.lines.values()]
      .map((line) => ({
        name: line.name,
        reference: line.reference || "",
        colorNeeds: [...(line.colorNeeds?.values() || [])].map((color) => ({
          ...color, totalQuantity: Number(color.totalQuantity.toFixed(4)),
        })),
        kind: line.kind,
        unit: line.unit,
        totalQuantity: Number(line.totalQuantity.toFixed(4)),
        totalCost: Number(line.totalCost.toFixed(4)),
      }))
      .sort((left, right) => left.kind.localeCompare(right.kind) || left.name.localeCompare(right.name)),
  }));

  result.sort((left, right) => {
    if (left.supplier === NO_SUPPLIER_LABEL) return 1;
    if (right.supplier === NO_SUPPLIER_LABEL) return -1;
    const orderDifference = groups.get(left.supplier).processOrder - groups.get(right.supplier).processOrder;
    if (orderDifference) return orderDifference;
    return left.supplier.localeCompare(right.supplier);
  });

  const grandTotalCost = result.reduce((sum, group) => sum + group.supplierTotalCost, 0);
  return { groups: result, grandTotalCost: Number(grandTotalCost.toFixed(4)) };
}


// Compact purchase view: one supplier/reference/unit, with each use contributing
// to a single total per material colour, even when cost-sheet line names differ.
export function buildCompactSupplierNeeds(item = {}) {
  const suppliers = new Map();
  const processOrder = (material) => {
    const value = item.supplierProcessOrder?.[material.process_source_line_id]
      ?? (item.processes || []).find(process => process.source_line_id === material.process_source_line_id)?.sort_order;
    return value != null && Number.isFinite(Number(value)) ? Number(value) : Infinity;
  };
  const materials = [...(item.supplierMaterials || item.materials || [])].sort((a, b) =>
    (processOrder(a) - processOrder(b) || Number(a.sort_order || 0) - Number(b.sort_order || 0)));
  const normalize = value => String(value || '').trim().toLocaleLowerCase('es');
  const swatch = name => {
    if (name === 'Sin color' || name === 'Color pendiente') return null;
    const settings = item.colorSettings || {};
    return colorSwatch(settings[name]) || colorSwatch(Object.values(settings).find(color => normalize(color?.description) === normalize(name)))
      || colorSwatch((item.colorLines || []).find(color => normalize(color.description) === normalize(name) || color.color === name));
  };
  for (const material of materials) {
    const supplier = material.supplier_name?.trim() || NO_SUPPLIER_LABEL;
    const supplierKey = material.supplier_id || supplier;
    if (!suppliers.has(supplierKey)) suppliers.set(supplierKey, { supplier, references: new Map() });
    const references = suppliers.get(supplierKey).references;
    const reference = material.supplier_ref || material.material_code || '';
    const unit = material.unit || 'Sin unidad';
    const key = JSON.stringify([reference || material.material_id || material.name, unit]);
    if (!references.has(key)) references.set(key, { reference, unit, name: material.supplier_description || material.name || 'Material', colors: new Map() });
    const entry = references.get(key);
    for (const need of materialColorNeeds(material, item.colorLines || [])) {
      const colorKey = JSON.stringify([need.color, need.pending]);
      if (!entry.colors.has(colorKey)) entry.colors.set(colorKey, { color: need.color, pending: need.pending, hex: swatch(need.color), order: Infinity, usages: new Map(), totalQuantity: 0 });
      const row = entry.colors.get(colorKey);
      const index = (item.colorLines || []).findIndex(line => materialColorAssignment(line, material).color === need.color);
      if (index >= 0) row.order = Math.min(row.order, index);
      const name = material.name || 'Material';
      row.usages.set(name, (row.usages.get(name) || 0) + need.totalQuantity);
      row.totalQuantity += need.totalQuantity;
    }
  }
  return [...suppliers.values()].map(group => ({ supplier: group.supplier, references: [...group.references.values()].map(entry => ({
    reference: entry.reference, unit: entry.unit, name: entry.name,
    colors: [...entry.colors.values()].sort((a,b) => a.order - b.order).map(row => ({ color: row.color, pending: row.pending, hex: row.hex,
      totalQuantity: Number(row.totalQuantity.toFixed(4)),
      usages: [...row.usages].map(([name, value]) => ({ name, totalQuantity: Number(value.toFixed(4)) })),
    })),
  })) }));
}
